import React, { useEffect, useState, useRef, createContext, useContext, Component } from 'react';
// Sound effect types
export type SoundType = 'click' | 'hover' | 'success' | 'error' | 'notification' | 'transition' | 'ambient';
// Sound effect configuration
const soundEffects = {
  click: 'https://assets.codepen.io/308367/click.mp3',
  hover: 'https://assets.codepen.io/308367/hover.mp3',
  success: 'https://assets.codepen.io/308367/success.mp3',
  error: 'https://assets.codepen.io/308367/error.mp3',
  notification: 'https://assets.codepen.io/308367/notification.mp3',
  transition: 'https://assets.codepen.io/308367/transition.mp3',
  ambient: 'https://assets.codepen.io/308367/ambient-space-track.mp3'
};
// Sound context
export const SoundContext = createContext({
  playSound: (sound: SoundType) => {},
  isMuted: false,
  toggleMute: () => {},
  volume: 0.5,
  setVolume: (volume: number) => {}
});
export const SoundProvider = ({
  children
}) => {
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(0.5);
  const audioRefs = useRef<Record<SoundType, HTMLAudioElement | null>>({
    click: null,
    hover: null,
    success: null,
    error: null,
    notification: null,
    transition: null,
    ambient: null
  });
  // Initialize audio elements
  useEffect(() => {
    // Create audio elements for each sound effect
    Object.entries(soundEffects).forEach(([key, url]) => {
      const audio = new Audio(url);
      audio.volume = volume;
      audio.preload = 'auto';
      audioRefs.current[key as SoundType] = audio;
    });
    // Special setup for ambient sound
    if (audioRefs.current.ambient) {
      audioRefs.current.ambient.loop = true;
    }
    // Cleanup
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.src = '';
        }
      });
    };
  }, []);
  // Update volume for all sounds when volume changes
  useEffect(() => {
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) {
        audio.volume = volume;
      }
    });
  }, [volume]);
  // Update mute state for all sounds when mute changes
  useEffect(() => {
    Object.values(audioRefs.current).forEach(audio => {
      if (audio) {
        audio.muted = isMuted;
      }
    });
  }, [isMuted]);
  // Play sound function
  const playSound = (sound: SoundType) => {
    const audio = audioRefs.current[sound];
    if (audio && !isMuted) {
      // Reset the audio to start from the beginning
      audio.currentTime = 0;
      audio.play().catch(error => {
        console.error(`Error playing sound ${sound}:`, error);
      });
    }
  };
  // Toggle mute function
  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };
  return <SoundContext.Provider value={{
    playSound,
    isMuted,
    toggleMute,
    volume,
    setVolume
  }} data-id="element-1320">
      {children}
    </SoundContext.Provider>;
};
// Hook to use sound effects
export const useSound = () => {
  const context = useContext(SoundContext);
  if (context === undefined) {
    throw new Error('useSound must be used within a SoundProvider');
  }
  return context;
};
// Component to render all audio elements
export const SoundEffects = () => {
  return <div className="hidden" data-id="element-1321">
      {Object.entries(soundEffects).map(([key, url]) => <audio key={key} id={`sound-${key}`} src={url} preload="auto" data-id="element-1322" />)}
    </div>;
};